/*
 * LogisticsAccommodationPage — booking-site-quality venue browser.
 *
 * Primary view: hero banner + city location grid + filterable hotel cards.
 * Toggle: "Open Map" switches to the existing MapLibre pin view.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bed, MapPinLine, PencilSimple, Plus,
  ListBullets, MapTrifold, MagnifyingGlass, Star,
  ArrowRight,
} from '@phosphor-icons/react';

import LogisticsLayout from './LogisticsLayout';
import { useLogisticsExercise } from '../../context/LogisticsExerciseContext';
import VenueMap, { VenueDetailCard } from './VenueMap';
import VenueCard from '../../components/logistics/VenueCard';
import AccommodationFilters from '../../components/logistics/AccommodationFilters';
import VenueDetailPanel from '../../components/logistics/VenueDetailPanel';
import VenueEditForm from '../../components/logistics/VenueEditForm';
import CheckoutDrawer from '../../components/logistics/CheckoutDrawer';
import logisticsApi from '../../lib/logisticsApi';
import { useWebSocket } from '../../context/WebSocketContext';

const PRESET_LOCATIONS = [
  { name: 'London',     center: [-0.13, 51.51],     zoom: 13, image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=600&h=400&fit=crop', country: 'United Kingdom' },
  { name: 'Manchester', center: [-2.244, 53.483],    zoom: 13, image: 'https://images.unsplash.com/photo-1515586838455-8f8f940d6853?w=600&h=400&fit=crop', country: 'United Kingdom' },
  { name: 'Edinburgh',  center: [-3.19, 55.95],      zoom: 13, image: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&h=400&fit=crop', country: 'Scotland' },
  { name: 'New York',   center: [-73.985, 40.748],   zoom: 13, image: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=600&h=400&fit=crop', country: 'United States' },
  { name: 'Tokyo',      center: [139.755, 35.68],    zoom: 13, image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&h=400&fit=crop', country: 'Japan' },
  { name: 'Sydney',     center: [151.209, -33.865],  zoom: 13, image: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=600&h=400&fit=crop', country: 'Australia' },
];

const ADMIN_ROLES = ['super_admin', 'hub_admin', 'simcel'];

const AccommodationContent = () => {
  const { getSocket } = useWebSocket();
  const { exercises, selected: ctxExercise, selectedId: exerciseId } = useLogisticsExercise();
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const [center, setCenter] = useState(PRESET_LOCATIONS[0].center);
  const [zoom, setZoom] = useState(PRESET_LOCATIONS[0].zoom);

  const [wallets, setWallets] = useState([]);

  // Panels
  const [checkoutVenue, setCheckoutVenue] = useState(null);
  const [checkoutRoomType, setCheckoutRoomType] = useState(null);
  const [detailPanelVenue, setDetailPanelVenue] = useState(null);
  const [editFormVenue, setEditFormVenue] = useState(null);
  const [editFormIsNew, setEditFormIsNew] = useState(false);
  const [newVenueCoords, setNewVenueCoords] = useState(null);

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [placeMode, setPlaceMode] = useState(false);

  // View toggle: 'cards' or 'map'
  const [viewMode, setViewMode] = useState('cards');

  // Browse state
  const [browseVenues, setBrowseVenues] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browseOffset, setBrowseOffset] = useState(0);
  const [presetCities, setPresetCities] = useState([]);

  // Filters
  const [selectedCities, setSelectedCities] = useState(['London']);
  const [searchText, setSearchText] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState(null);
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [starsMin, setStarsMin] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Location search (worldwide)
  const [locationQuery, setLocationQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeLocation, setActiveLocation] = useState(null);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const [browseError, setBrowseError] = useState(null);
  const suggestionsRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const skipAutocompleteRef = React.useRef(false);

  // User role check
  const userRoles = useMemo(() => {
    try {
      const token = localStorage.getItem('ems_token');
      if (!token) return [];
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.roles || [];
    } catch { return []; }
  }, []);
  const isAdmin = useMemo(() => userRoles.some((r) => ADMIN_ROLES.includes(r)), [userRoles]);

  const exercise = ctxExercise;

  useEffect(() => {
    if (!exerciseId) return;
    logisticsApi.presetCities(exerciseId)
      .then((r) => setPresetCities(r.cities || []))
      .catch(() => setPresetCities(PRESET_LOCATIONS.map((l) => l.name)));
  }, [exerciseId]);

  // Autocomplete: debounce location queries
  useEffect(() => {
    if (skipAutocompleteRef.current) {
      skipAutocompleteRef.current = false;
      return;
    }
    if (!locationQuery || locationQuery.length < 2 || !exerciseId) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setGeocodeLoading(true);
      try {
        const res = await logisticsApi.geocodeSearch(exerciseId, locationQuery);
        setSuggestions(res.suggestions || []);
        setShowSuggestions(true);
      } catch { setSuggestions([]); }
      finally { setGeocodeLoading(false); }
    }, 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [locationQuery, exerciseId]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadWallets = useCallback(async () => {
    if (!exerciseId) { setWallets([]); return; }
    try {
      const w = await logisticsApi.myWallets();
      const all = [...(w.personal || []), ...(w.team || [])];
      setWallets(all.filter((x) => x.logistics_exercise_id === exerciseId && !x.frozen));
    } catch {
      setWallets([]);
    }
  }, [exerciseId]);

  useEffect(() => { loadWallets(); }, [loadWallets]);

  // Browse venues
  const doBrowse = useCallback(async (append = false, locationOverride = null) => {
    if (!exerciseId) return;
    const loc = locationOverride || activeLocation;
    if (!loc && selectedCities.length === 0) return;
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      const offset = append ? browseOffset + 24 : 0;
      const filters = {
        category: 'hotel',
        subcategories: subcategoryFilter ? [subcategoryFilter] : [],
        min_price: priceMin ? priceMin * 100 : null,
        max_price: priceMax ? priceMax * 100 : null,
        stars_min: starsMin,
        search: searchText || null,
        limit: 24,
        offset,
      };
      if (loc) {
        filters.location = loc;
        filters.cities = [];
      } else {
        filters.cities = selectedCities;
      }
      const res = await logisticsApi.browseVenues(exerciseId, filters);
      if (res.error) {
        setBrowseError(res.error);
        if (!append) setBrowseVenues([]);
        setBrowseTotal(0);
      } else {
        if (append) {
          setBrowseVenues((prev) => [...prev, ...(res.venues || [])]);
        } else {
          setBrowseVenues(res.venues || []);
        }
        setBrowseTotal(res.total || 0);
        if (res.geocoded) {
          setActiveLocation(res.geocoded.name);
          setSelectedCities(res.cities || [res.geocoded.name]);
        }
      }
      setBrowseOffset(offset);
    } catch (e) {
      console.error('[Browse] failed:', e.message);
      setBrowseError(e.message);
      if (!append) setBrowseVenues([]);
    } finally {
      setBrowseLoading(false);
    }
  }, [exerciseId, selectedCities, activeLocation, subcategoryFilter, priceMin, priceMax, starsMin, searchText, browseOffset]);

  // Initial load
  useEffect(() => {
    if (exerciseId && !initialLoadDone) {
      setInitialLoadDone(true);
      doBrowse(false);
    }
  }, [exerciseId, initialLoadDone, doBrowse]);

  const upsertWallet = useCallback((wallet) => {
    if (!wallet?.wallet_id) return;
    setWallets((prev) => prev.map((w) => (w.wallet_id === wallet.wallet_id ? { ...w, ...wallet } : w)));
  }, []);

  useEffect(() => {
    const socket = getSocket?.();
    if (!socket) return;
    const onEvent = (data) => {
      if (data?.booking?.logistics_exercise_id && data.booking.logistics_exercise_id !== exerciseId) return;
      upsertWallet(data?.wallet);
    };
    const events = ['ls_booking_created', 'ls_booking_confirmed', 'ls_booking_cancelled'];
    events.forEach((e) => socket.on(e, onEvent));
    return () => events.forEach((e) => socket.off(e, onEvent));
  }, [getSocket, exerciseId, upsertWallet]);

  const totalAvailable = useMemo(
    () => wallets.reduce((s, w) => s + (w.available || 0), 0),
    [wallets],
  );

  const jumpTo = (loc) => {
    setCenter(loc.center);
    setZoom(loc.zoom);
    setSelectedVenue(null);
    setMapKey((k) => k + 1);
  };

  const [pendingBrowse, setPendingBrowse] = useState(false);

  const handleCityClick = (cityName) => {
    setSelectedCities([cityName]);
    setActiveLocation(null);
    setLocationQuery('');
    setViewMode('cards');
    setBrowseOffset(0);
    setPendingBrowse(true);
  };

  const handleLocationSelect = (suggestion) => {
    skipAutocompleteRef.current = true;
    setLocationQuery(suggestion.name);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveLocation(suggestion.name);
    setSelectedCities([]);
    setViewMode('cards');
    setBrowseOffset(0);
    doBrowse(false, suggestion.name);
  };

  const handleLocationSearch = () => {
    if (!locationQuery.trim()) return;
    setShowSuggestions(false);
    setActiveLocation(locationQuery.trim());
    setSelectedCities([]);
    setBrowseOffset(0);
    doBrowse(false, locationQuery.trim());
  };

  useEffect(() => {
    if (pendingBrowse && exerciseId) {
      setPendingBrowse(false);
      doBrowse(false);
    }
  }, [pendingBrowse, selectedCities, exerciseId, doBrowse]);

  const bookDisabledReason = useMemo(() => {
    if (wallets.length === 0) return 'No spendable wallet for this exercise.';
    if (exercise && !['active', 'draft'].includes(exercise.status)) {
      return `Exercise '${exercise.name}' is ${exercise.status} — all bookings are closed regardless of venue status.`;
    }
    return null;
  }, [wallets, exercise]);

  const handleViewDetails = (venue) => setDetailPanelVenue(venue);
  const handleBookRoomType = (venue, roomType) => {
    setDetailPanelVenue(null);
    setCheckoutVenue(venue);
    setCheckoutRoomType(roomType);
  };
  const handleQuickBook = (venue) => {
    setCheckoutVenue(venue);
    setCheckoutRoomType(null);
  };
  const handleEditVenue = (venue) => {
    setEditFormVenue(venue);
    setEditFormIsNew(false);
    setNewVenueCoords(null);
  };
  const handleAddVenue = () => {
    setEditFormVenue(null);
    setEditFormIsNew(true);
    setNewVenueCoords(null);
  };
  const handleMapClick = (coords) => {
    if (!placeMode) return;
    setNewVenueCoords(coords);
    setEditFormVenue(null);
    setEditFormIsNew(true);
    setPlaceMode(false);
  };
  const handleVenueSaved = () => {
    setMapKey((k) => k + 1);
    doBrowse(false);
  };

  const currencySymbol = exercise?.currency_symbol || 'Ÿ';

  return (
    <>
      {/* ─── Hero Banner ─── */}
      <section className="ls-hero-banner ls-hero-responsive relative overflow-hidden rounded-xl sm:rounded-2xl mb-4 sm:mb-8">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1400&h=400&fit=crop&q=80)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to right, rgba(10,13,16,0.88) 0%, rgba(10,13,16,0.5) 60%, rgba(10,13,16,0.2) 100%)' }}
        />
        <div className="relative z-10 h-full flex flex-col justify-center px-4 py-5 sm:px-8 sm:py-8 md:px-12">
          <span className="flex items-center gap-2 mb-2 sm:mb-3 text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase" style={{ color: 'var(--ls-primary)' }}>
            <Bed size={14} weight="fill" /> ACCOMMODATION
          </span>
          <h1
            className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-1.5 sm:mb-3"
            style={{ color: '#fff', fontFamily: 'var(--ls-font-heading)' }}
          >
            Find your perfect stay
          </h1>
          <p className="text-xs sm:text-sm md:text-base max-w-lg hidden sm:block" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Browse real hotels across the globe. Book with simulated funds and
            watch the ledger record every hold and capture.
          </p>

          {/* Hero controls */}
          <div className="flex items-center gap-2 sm:gap-3 mt-3 sm:mt-5">
            {isAdmin && (
              <button
                onClick={() => { setEditMode(!editMode); setPlaceMode(false); }}
                className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-lg font-semibold transition-all"
                style={{
                  background: editMode ? 'var(--ls-primary)' : 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  border: '1px solid ' + (editMode ? 'var(--ls-primary)' : 'rgba(255,255,255,0.25)'),
                  backdropFilter: 'blur(8px)',
                }}
                data-testid="ls-edit-mode-toggle"
              >
                <PencilSimple size={14} weight={editMode ? 'fill' : 'bold'} />
                {editMode ? 'Exit Edit Mode' : 'Edit Mode'}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Edit mode banner */}
      {editMode && (
        <div
          className="mb-4 sm:mb-6 px-3 sm:px-5 py-2.5 sm:py-3 rounded-xl flex flex-wrap items-center gap-2 sm:gap-3 text-xs"
          style={{
            background: 'linear-gradient(135deg, rgba(234,179,8,0.15) 0%, rgba(234,179,8,0.05) 100%)',
            border: '1px solid var(--ls-warning)',
            color: 'var(--ls-secondary)',
          }}
          data-testid="ls-edit-banner"
        >
          <PencilSimple size={14} weight="fill" style={{ color: 'var(--ls-warning)' }} />
          <span className="font-semibold">EDIT MODE</span>
          <span style={{ color: 'var(--ls-muted)' }}>— Click venues to edit. Changes save as exercise overrides.</span>
          <div className="flex-1" />
          {viewMode === 'cards' && (
            <button onClick={handleAddVenue} className="ls-btn-primary !py-2 text-xs flex items-center gap-1.5" data-testid="ls-add-venue-btn">
              <Plus size={12} weight="bold" /> Add Venue
            </button>
          )}
          {viewMode === 'map' && (
            <button
              onClick={() => setPlaceMode(!placeMode)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold ${placeMode ? 'ls-btn-primary' : 'ls-btn-ghost'}`}
              data-testid="ls-place-mode-btn"
            >
              <Plus size={12} weight="bold" />
              {placeMode ? 'Click map to place...' : 'Add Venue (Map)'}
            </button>
          )}
        </div>
      )}

      {/* ─── Top Hotel Locations ─── */}
      {exerciseId && viewMode === 'cards' && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="ls-heading text-xl font-bold">Top Hotel Locations</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('map')}
                className="ls-btn-ghost !py-2 text-xs flex items-center gap-1.5"
              >
                <MapTrifold size={14} weight="bold" /> Open Map
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
            {PRESET_LOCATIONS.map((loc) => (
              <motion.div
                key={loc.name}
                className="ls-city-card relative overflow-hidden rounded-xl cursor-pointer group"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.25 }}
                onClick={() => handleCityClick(loc.name)}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${loc.image})` }}
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 60%)' }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
                  <h3 className="font-bold text-sm leading-tight" style={{ color: '#fff', fontFamily: 'var(--ls-font-heading)' }}>
                    {loc.name}
                  </h3>
                  <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.65)' }}>
                    {loc.country}
                  </p>
                </div>
                {selectedCities.includes(loc.name) && (
                  <div
                    className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--ls-primary)' }}
                  >
                    <span className="text-[10px] font-bold text-white">✓</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ─── Search & Filters Bar ─── */}
      {exerciseId && viewMode === 'cards' && (
        <>
          {/* Location search row */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mb-4">
            <div className="relative flex-1" ref={suggestionsRef}>
              <MapPinLine
                size={16} weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10"
                style={{ color: 'var(--ls-primary)' }}
              />
              <input
                type="text"
                className="ls-input w-full !pl-10 !py-3 !rounded-xl text-sm"
                placeholder="Search any city worldwide — Delhi, Paris, Dubai, Bali..."
                value={locationQuery}
                onChange={(e) => { setLocationQuery(e.target.value); setShowSuggestions(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLocationSearch(); }}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                data-testid="ls-location-input"
              />
              {geocodeLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--ls-border)', borderTopColor: 'var(--ls-primary)' }} />
                </div>
              )}

              {/* Autocomplete suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  className="absolute top-full left-0 right-0 mt-1 z-50 ls-surface shadow-xl overflow-hidden"
                  style={{ border: '1px solid var(--ls-border)', borderRadius: '12px' }}
                >
                  {suggestions.map((s, i) => (
                    <button
                      key={`${s.name}-${s.country}-${i}`}
                      className="w-full text-left px-4 py-3 flex items-center gap-3 transition-colors hover:bg-[var(--ls-accent)]"
                      onClick={() => handleLocationSelect(s)}
                    >
                      <MapPinLine size={16} weight="fill" style={{ color: 'var(--ls-primary)' }} className="flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="font-semibold text-sm block">{s.name}</span>
                        <span className="text-[11px] block truncate" style={{ color: 'var(--ls-muted)' }}>
                          {s.country}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="ls-btn-primary !py-3 !px-6 !rounded-xl text-sm font-bold flex-1 sm:flex-none justify-center"
                onClick={handleLocationSearch}
                disabled={browseLoading || !locationQuery.trim()}
                data-testid="ls-search-btn"
              >
                {browseLoading ? 'Searching...' : 'Search'}
              </button>
              <button
                className={`ls-btn-ghost !py-3 !px-4 !rounded-xl text-sm ${showFilters ? 'ls-view-toggle-active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Star size={14} weight="bold" /> Filters
              </button>
            </div>
          </div>

          {/* Active location indicator */}
          {activeLocation && (
            <div className="flex items-center gap-2 mb-3 text-xs" style={{ color: 'var(--ls-muted)' }}>
              <MapPinLine size={12} weight="fill" style={{ color: 'var(--ls-primary)' }} />
              <span>Showing hotels in <strong style={{ color: 'var(--ls-secondary)' }}>{activeLocation}</strong></span>
              <button
                className="text-[10px] px-2 py-0.5 rounded-full hover:bg-[var(--ls-accent)] transition-colors"
                style={{ border: '1px solid var(--ls-border)' }}
                onClick={() => {
                  setActiveLocation(null);
                  setLocationQuery('');
                  setSelectedCities(['London']);
                  setBrowseOffset(0);
                  setPendingBrowse(true);
                }}
              >
                Clear
              </button>
            </div>
          )}

          {/* Browse error */}
          {browseError && (
            <div
              className="mb-4 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
              style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid var(--ls-danger)', color: 'var(--ls-danger)' }}
            >
              <span>{browseError}</span>
            </div>
          )}

          {/* Expandable filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <AccommodationFilters
                  cities={presetCities.length > 0 ? presetCities : PRESET_LOCATIONS.map((l) => l.name)}
                  selectedCities={selectedCities}
                  onCitiesChange={setSelectedCities}
                  priceMin={priceMin}
                  priceMax={priceMax}
                  onPriceChange={(which, val) => which === 'min' ? setPriceMin(val) : setPriceMax(val)}
                  subcategory={subcategoryFilter}
                  onSubcategoryChange={setSubcategoryFilter}
                  starsMin={starsMin}
                  onStarsMinChange={setStarsMin}
                  searchText={searchText}
                  onSearchChange={setSearchText}
                  onApply={() => { setBrowseOffset(0); doBrowse(false); }}
                  onClear={() => {
                    setSelectedCities(['London']);
                    setSearchText('');
                    setSubcategoryFilter(null);
                    setPriceMin(null);
                    setPriceMax(null);
                    setStarsMin(null);
                  }}
                  loading={browseLoading}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* ─── Results toolbar ─── */}
      {exerciseId && (
        <div className="flex items-center justify-between mb-3 sm:mb-5">
          <div className="flex items-center gap-2 sm:gap-3">
            {viewMode === 'cards' && (
              <h2 className="ls-heading text-sm sm:text-lg font-bold">
                {browseLoading
                  ? 'Searching...'
                  : browseTotal > 0
                    ? `${browseTotal} hotel${browseTotal !== 1 ? 's' : ''} found`
                    : activeLocation || selectedCities.length > 0
                      ? 'No results'
                      : 'Results'}
              </h2>
            )}
            {viewMode === 'map' && (
              <h2 className="ls-heading text-lg font-bold">Explore on Map</h2>
            )}
          </div>

          <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: 'var(--ls-elevated)', border: '1px solid var(--ls-border)' }}>
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'cards' ? 'ls-view-toggle-active' : ''}`}
              data-testid="ls-view-cards"
            >
              <ListBullets size={14} weight="bold" /> Cards
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'map' ? 'ls-view-toggle-active' : ''}`}
              data-testid="ls-view-map"
            >
              <MapTrifold size={14} weight="bold" /> Map
            </button>
          </div>
        </div>
      )}

      {/* ─── Content area ─── */}
      {exerciseId ? (
        <AnimatePresence mode="wait">
          {viewMode === 'cards' ? (
            <motion.div
              key="cards"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              {browseLoading && browseVenues.length === 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="ls-skeleton rounded-xl" style={{ height: '320px' }} />
                  ))}
                </div>
              ) : browseVenues.length === 0 ? (
                <div className="ls-elevated p-8 sm:p-16 text-center rounded-xl">
                  <Bed size={48} weight="duotone" style={{ color: 'var(--ls-primary)' }} className="mx-auto mb-3 sm:mb-4" />
                  <p className="ls-heading text-lg sm:text-xl font-bold mb-2">No hotels found</p>
                  <p className="text-xs sm:text-sm max-w-md mx-auto mb-4 sm:mb-6" style={{ color: 'var(--ls-muted)' }}>
                    {activeLocation
                      ? `No hotels found in ${activeLocation}. The venue provider may be temporarily unavailable — try again or search a different location.`
                      : 'Try selecting a different city from the locations above, or search any city worldwide.'}
                  </p>
                  <button
                    className="ls-btn-primary"
                    onClick={() => {
                      setActiveLocation(null);
                      setLocationQuery('');
                      setSelectedCities(['London']);
                      setBrowseOffset(0);
                      setPendingBrowse(true);
                    }}
                  >
                    Browse London Hotels
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
                    {browseVenues.map((v) => (
                      <VenueCard
                        key={v.venue_id}
                        venue={v}
                        onViewDetails={handleViewDetails}
                        onBook={handleQuickBook}
                        onEdit={handleEditVenue}
                        editMode={editMode}
                        bookDisabledReason={bookDisabledReason}
                        currencySymbol={currencySymbol}
                      />
                    ))}
                  </div>
                  {browseVenues.length < browseTotal && (
                    <div className="text-center mt-8">
                      <button
                        className="ls-btn-ghost !px-8 !py-3 !rounded-xl font-semibold flex items-center gap-2 mx-auto"
                        onClick={() => doBrowse(true)}
                        disabled={browseLoading}
                        data-testid="ls-load-more"
                      >
                        {browseLoading ? 'Loading...' : (
                          <>Load more hotels <ArrowRight size={14} weight="bold" /></>
                        )}
                      </button>
                      <p className="text-xs mt-2" style={{ color: 'var(--ls-muted)' }}>
                        Showing {browseVenues.length} of {browseTotal}
                      </p>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="map"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              {/* Preset location chips */}
              <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4 overflow-x-auto pb-1 scrollbar-hide">
                <MapPinLine size={14} weight="bold" className="flex-shrink-0" style={{ color: 'var(--ls-muted)' }} />
                <span className="ls-overline flex-shrink-0" style={{ color: 'var(--ls-muted)' }}>Jump to:</span>
                {PRESET_LOCATIONS.map((loc) => (
                  <button
                    key={loc.name}
                    onClick={() => jumpTo(loc)}
                    className="text-xs px-3 py-1.5 rounded-full transition-all whitespace-nowrap flex items-center gap-1.5"
                    style={{
                      background: 'var(--ls-elevated)',
                      color: 'var(--ls-secondary)',
                      border: '1px solid var(--ls-border)',
                    }}
                    data-testid={`ls-preset-${loc.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <MapPinLine size={11} weight="fill" />
                    {loc.name}
                  </button>
                ))}
              </div>

              <motion.section
                key={mapKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.35 }}
                className="grid grid-cols-1 lg:grid-cols-3 gap-4"
              >
                <div className={`lg:col-span-2 ${placeMode ? 'ls-place-mode' : ''}`}>
                  <VenueMap
                    exerciseId={exerciseId}
                    category="hotel"
                    center={center}
                    zoom={zoom}
                    selectedVenueId={selectedVenue?.venue_id}
                    onSelect={setSelectedVenue}
                    height="min(620px, 60vh)"
                    onMapClick={editMode ? handleMapClick : undefined}
                    placeMode={placeMode}
                  />
                </div>
                <div className="lg:col-span-1">
                  <VenueDetailCard
                    venue={selectedVenue}
                    onBook={handleQuickBook}
                    bookDisabledReason={bookDisabledReason}
                    onViewDetails={handleViewDetails}
                    onEdit={handleEditVenue}
                    editMode={editMode}
                  />
                </div>
              </motion.section>
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        <div className="ls-elevated p-8 sm:p-16 text-center rounded-xl">
          <Bed size={48} weight="duotone" style={{ color: 'var(--ls-primary)' }} className="mx-auto mb-3 sm:mb-4" />
          <p className="ls-heading text-lg sm:text-xl font-bold mb-2">No logistics exercise found</p>
          <p className="text-xs sm:text-sm max-w-md mx-auto" style={{ color: 'var(--ls-muted)' }}>
            An admin needs to create at least one logistics exercise so the venue
            browser can scope overrides and custom regions.
          </p>
        </div>
      )}

      {/* Overlays */}
      <VenueDetailPanel
        open={!!detailPanelVenue}
        onClose={() => setDetailPanelVenue(null)}
        venue={detailPanelVenue}
        exerciseId={exerciseId}
        onBookRoomType={handleBookRoomType}
        bookDisabledReason={bookDisabledReason}
        currencySymbol={currencySymbol}
      />

      <CheckoutDrawer
        open={!!checkoutVenue}
        onClose={() => { setCheckoutVenue(null); setCheckoutRoomType(null); }}
        exerciseId={exerciseId}
        venue={checkoutVenue}
        wallets={wallets}
        currencySymbol={currencySymbol}
        onBooked={(_booking, wallet) => { upsertWallet(wallet); }}
        roomType={checkoutRoomType}
      />

      <VenueEditForm
        open={!!editFormVenue || editFormIsNew}
        onClose={() => { setEditFormVenue(null); setEditFormIsNew(false); setNewVenueCoords(null); }}
        venue={editFormVenue}
        exerciseId={exerciseId}
        onSaved={handleVenueSaved}
        isNew={editFormIsNew}
        initialLat={newVenueCoords?.lat}
        initialLng={newVenueCoords?.lng}
      />
    </>
  );
};

const LogisticsAccommodationPage = () => (
  <LogisticsLayout>
    <AccommodationContent />
  </LogisticsLayout>
);

export default LogisticsAccommodationPage;
